import _ from 'lodash'
import type {CssValue} from '../../constants'
import {deserializeUnit, serializeUnit} from '../utils/unit'
import {hasValidKeys} from '../utils/object'
import type {CropMask, CropMaskFlip, CropMaskFlipArray} from '@wix/document-services-types'
import {deserializeUrl, serializeUrl} from '../utils/url'

const FLIP_OPTIONS: CropMaskFlipArray = ['x', 'y', 'xy', 'none']
const FLIP_OPTIONS_SET = new Set<string>(FLIP_OPTIONS)
const ROTATE_UNIT_TYPE = 'deg'
const VALID_UNIT_TYPES_SET = new Set([ROTATE_UNIT_TYPE])
const VALID_KEYS_SET = new Set(['svgId', 'flip', 'rotate'])

const isFlip = (val: string): val is CropMaskFlip => FLIP_OPTIONS_SET.has(val)

const deserializeRotate = (val: CssValue): CropMask['rotate'] | undefined =>
    deserializeUnit(val, VALID_UNIT_TYPES_SET)?.value

const serialize = (val: any): CssValue | undefined => {
    if (!_.isPlainObject(val) || !hasValidKeys(val, VALID_KEYS_SET)) {
        return undefined
    }

    const {svgId, flip, rotate} = val

    const props = []

    if (!_.isString(svgId)) {
        return undefined
    }

    const svgUrlFn = serializeUrl(svgId)
    props.push(svgUrlFn)

    if (flip !== undefined) {
        if (!_.isString(flip)) {
            return undefined
        }

        props.push(flip)
    }

    if (rotate !== undefined) {
        if (!_.isNumber(rotate)) {
            return undefined
        }

        props.push(serializeUnit({value: rotate, type: ROTATE_UNIT_TYPE}, VALID_UNIT_TYPES_SET))
    }

    return props.join(' ')
}

const deserialize = (rule: CssValue): CropMask | undefined => {
    const props = rule.split(' ')

    if (props.length > 3 || props.length < 1) {
        return undefined
    }

    const [svgUrlFn, prop2, prop3] = props

    const svgId = deserializeUrl(svgUrlFn)
    if (_.isNil(svgId)) {
        return undefined
    }

    const cropMask: CropMask = {svgId}

    if (prop2) {
        if (isFlip(prop2)) {
            cropMask.flip = prop2
        } else {
            if (prop3 !== undefined) {
                return undefined
            }

            const rotate = deserializeRotate(prop2)

            if (rotate === undefined) {
                return undefined
            }

            cropMask.rotate = rotate
        }
    }

    if (prop3) {
        const rotate = deserializeRotate(prop3)

        if (rotate === undefined) {
            return undefined
        }

        cropMask.rotate = rotate
    }

    return cropMask
}

export default {
    serialize,
    deserialize
}
